import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	formatToMK,
	getPreserveTreeState,
	renderProfilePic,
	setDefaultValueForTree,
	toggleTreeList
} from '@features/crm/utils/helpers';
import { useFinalizeTargetMutation, useGetCRMTargetListQuery } from '@features/crm/v3/target/api/targetApi';
import TargetDetailsModal from '@features/crm/v3/target/components/TargetDetailsModal';

import LoaderComponent from '@/components/LoaderComponent';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { toastSuccess } from '@/components/Toast';
import { checkUserType, getUserInfo, printApiError } from '@/utils/Util';
import CommonImage from '@/components/CommonImage';
import { setSelectedCRMTarget, setIsPreserveTreeState } from '@/store/actions/crm/CRMCommonAction';

import ConfirmationModal from '../../components/ConfirmationModal';

const TargetList = () => {
	const filterData = useSelector((store) => store.filterFieldStore);
	const isPreserveTreeState = useSelector((store) => store.crmCommonStore.isPreserveTreeState);
	const [openTargetDetailsModal, setOpenTargetDetailsModal] = useState(false);
	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [selectedTargetId, setSelectedTargetId] = useState(null);
	const [data, setData] = useState([]);
	const dispatch = useDispatch();
	const [finalizeTarget] = useFinalizeTargetMutation();

	const generateParams = () => {
		let params = {};
		if (filterData?.quarter) {
			params.quarter = filterData['quarter'][0]?.value;
		}
		return params;
	};

	const {
		data: apiData,
		isLoading,
		refetch
	} = useGetCRMTargetListQuery(generateParams(), {
		skip: !filterData?.quarter
	});

	useEffect(() => {
		if (filterData?.quarter) {
			refetch();
		}
	}, [filterData?.quarter]);

	useEffect(() => {
		if (apiData) {
			const dataCopy = JSON.parse(JSON.stringify(apiData));
			const modifiedData = setDefaultValueForTree(
				dataCopy,
				'childTargetResponseList',
				isPreserveTreeState
			);

			if (data.length > 0 && isPreserveTreeState) {
				const preservedData = getPreserveTreeState(modifiedData, data);
				setData(preservedData);
			} else {
				setData(modifiedData);
			}
		}
	}, [apiData, isPreserveTreeState]);

	const returnChildTarget = (item) => {
		return item?.childTargetResponseList?.map((target, index) => {
			return renderCard(target, index, false);
		});
	};

	const toggleTreeNode = (isRootNode, nodeInfo) => {
		const modifiedData = toggleTreeList(data, isRootNode, nodeInfo, 'childTargetResponseList');
		setData(modifiedData);
	};

	const handleFinalizeTarget = async () => {
		try {
			dispatch(openLoader());
			const response = await finalizeTarget(selectedTargetId).unwrap();
			if (response?.success) {
				dispatch(setIsPreserveTreeState(true));
				toastSuccess('Target finalized successfully');
			}
			setOpenConfirmationModal(false);
			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
			dispatch(closeLoader());
		}
	};

	const isFinalizedDisabled = (item) => {
		return (
			(item?.isFinalized === undefined || item?.userId !== getUserInfo().id) && !checkUserType('ADMIN')
		);
	};

	const renderCard = (item, idx, isRootNode = false) => {
		if (item.isOpen) {
			return (
				<li className="opened view" key={`tree_${idx}`}>
					<div
						className={`single-target-component ${!item?.id ? 'empty-target' : ''} ${
							isRootNode ? 'parent' : ''
						}`}
					>
						{item?.childTargetResponseList?.length > 0 && (
							<div className="expand-action">
								<button
									className="button text-only"
									onClick={() => toggleTreeNode(isRootNode, item)}
								>
									{item.isChildOpen && (
										<CommonImage src="/icons/row-close.svg" alt="people" />
									)}
									{!item.isChildOpen && (
										<CommonImage src="/icons/row-open.svg" alt="people" />
									)}
								</button>
							</div>
						)}
						<div className="assignee-and-status">
							<div className="d-flex align-items-center gap-2">
								{renderProfilePic(item)}
								<p className="regular-14 gray_dark_01">{item?.userName}</p>
							</div>
						</div>

						<div className="brand-info d-flex align-items-center gap-4">
							{item?.childTargetResponseList?.length > 0 && (
								<>
									<p className="regular-14">Team Target :</p>
									<div>
										<p className="regular-14 mb-1">
											{item?.teamTargetNoOfNitexDesign} Design
										</p>
										<p className="regular-14">
											${formatToMK(item?.teamTargetAmount)} Sales
										</p>
									</div>
								</>
							)}
						</div>
						<div className="nitex-design-info d-flex align-items-center gap-4">
							<p className="regular-14">Individual Target : </p>
							<div>
								<p className="regular-14 mb-1">{item?.targetNoOfNitexDesign} Design</p>
								<p className="regular-14"> ${formatToMK(item?.targetAmount)} Sales</p>
							</div>
						</div>
						<div className="sales-info">
							{item?.isFinalized ? (
								<CommonImage
									src="/icons/completed-icon.svg"
									alt="completed"
									className="w-24"
								/>
							) : (
								<button
									onClick={() => {
										setSelectedTargetId(item?.salesTargetId);
										setOpenConfirmationModal(true);
									}}
									className="button text-only blue"
									disabled={isFinalizedDisabled(item)}
								>
									Finalize
								</button>
							)}
						</div>

						<div className="actions">
							<button
								onClick={() => {
									dispatch(setSelectedCRMTarget(item));
									setOpenTargetDetailsModal(true);
								}}
								className="button text-only blue"
							>
								Details
							</button>
						</div>
					</div>
					<ul>{returnChildTarget(item)}</ul>
				</li>
			);
		}
	};

	const renderTargets = () => {
		return data?.map((item, index) => {
			return <React.Fragment key={`tree_${index}`}>{renderCard(item, index, true)}</React.Fragment>;
		});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="target-list">
				<div className="crm-data-tree">
					<div className="tree">
						<ul className="parent-layer">{renderTargets()}</ul>
					</div>
				</div>
			</div>

			{openTargetDetailsModal && (
				<TargetDetailsModal onCloseModal={() => setOpenTargetDetailsModal(false)} />
			)}
			{openConfirmationModal && (
				<ConfirmationModal
					title={'Finalize Target'}
					closeModal={() => setOpenConfirmationModal(false)}
					description={` to finalize this target?`}
					callback={handleFinalizeTarget}
				/>
			)}
		</LoaderComponent>
	);
};

export default TargetList;
